import React, { useEffect, useState, FC } from 'react';
import {
  FloatingLabel,
  Form,
  FormCheck,
  FormControl,
  FormGroup,
  FormLabel,
  FormSelect,
} from 'react-bootstrap';
import { useFormik } from 'formik';
import { NewTariffFeature } from '../tariff.types';
import EditorWrapper from '../../editor';
import * as yup from 'yup';
import cls from './CreateOrChangeTariffFeatureForm.module.scss';
import { useNavigate } from 'react-router-dom';

type CreateOrChangeTariffFeatureFormProps = {
  submitHandler: (feature: NewTariffFeature) => Promise<void>;
  className: string;
  initialValues?: {
    name: string;
    description: string;
    priority: number;
    show: boolean;
    icon: NewTariffFeature['icon'];
    colorIcon: string;
  };
};

const defaultValues = {
  name: '',
  description: '',
  priority: 0,
  show: false,
  icon: null,
  colorIcon: '',
};

const CreateOrChangeTariffFeatureForm: FC<CreateOrChangeTariffFeatureFormProps> = ({
  initialValues = defaultValues,
  submitHandler,
  className,
}) => {
  const [disabled, setDisabled] = useState<boolean>(true);
  const navigate = useNavigate();
  const [text, setText] = useState<string>(initialValues.description ?? '');
  const [featureType, setFeatureType] = useState<NewTariffFeature['icon'] | null>(
    initialValues.icon || null,
  );
  const validationSchema = yup.object().shape({
    name: yup.string().required('This field is required'),
    priority: yup.number().min(0, 'Minimum value is 0'),
  });
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = (e: any) => {
      if (e.target.closest('.tox-tinymce-aux, .moxman-window, .tam-assetmanager-root') !== null) {
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('focusin', handler);
    return () => document.removeEventListener('focusin', handler);
  }, []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values) => {
      if (featureType === null) {
        return;
      }
      // NOTE: Temp fix.
      //  Better solution is to use editor wrapper to get text without tags
      const textWithoutTags = text.replace(/<[^>]*>/g, '');

      const newFeature = {
        ...values,
        priority: Number(values.priority),
        description: textWithoutTags,
        icon: featureType,
        colorIcon: 'red',
      };
      submitHandler(newFeature);
    },
  });

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      setFeatureType(null);
    } else {
      setFeatureType(value as NewTariffFeature['icon']);
    }
  };

  useEffect(() => {
    if (
      formik.values.name === initialValues.name &&
      formik.values.priority === initialValues.priority &&
      text === initialValues.description &&
      featureType === initialValues.icon &&
      formik.values.show === initialValues.show
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [
    formik.values.name,
    formik.values.priority,
    formik.values.show,
    text,
    featureType,
    initialValues,
    setDisabled,
  ]);

  return (
    <Form
      className={`${className} py-3 pr-5 d-flex flex-column gap-3`}
      onSubmit={formik.handleSubmit}
      data-testid="feature-form"
      noValidate
    >
      <div className={cls.InputsWrapper}>
        <div className={cls.InputWrapper}>
          <FloatingLabel controlId="name" label="Feature Name*">
            <FormControl
              type="text"
              onChange={formik.handleChange}
              name="name"
              value={formik.values.name}
              data-testid="feature-preview"
              isInvalid={!!formik.errors.name}
            />
          </FloatingLabel>
          {formik.errors.name && (
            <span data-testid="feature-name-error" className={cls.Feedback}>
              {formik.errors.name}
            </span>
          )}
        </div>

        <div className={cls.InputWrapper}>
          <FloatingLabel controlId="priority" label="Priority">
            <FormControl
              type="text"
              onChange={formik.handleChange}
              name="priority"
              value={formik.values.priority}
              data-testid="feature-priority"
              isInvalid={!!formik.errors.priority}
            />
          </FloatingLabel>
          {formik.errors.priority && (
            <span data-testid="feature-name-error" className={cls.Feedback}>
              Value must be a number
            </span>
          )}
        </div>
        <FloatingLabel controlId="type" label="Feature Type*" className={cls.InputWrapper}>
          <FormSelect name="type" onChange={handleTypeChange} value={featureType ?? ''}>
            <option value="">Select type</option>
            <option value="ROBOT">Robots</option>
            <option value="AUTOMATION">Connections</option>
            <option value="SUPPORT_AGENT">Support</option>
          </FormSelect>
        </FloatingLabel>
        <FormGroup className={cls.Switcher}>
          <FormCheck
            type="switch"
            checked={formik.values.show}
            onChange={() => formik.setFieldValue('show', !formik.values.show)}
            data-testid="feature-show"
            id="show"
          />
          <FormLabel htmlFor="show" className="regular-text mt-2">
            Visible on card
          </FormLabel>
        </FormGroup>
      </div>
      <div className={cls.TextareaWrapper}>
        <EditorWrapper setText={setText} value={text} />
      </div>
      <div className="d-flex gap-3">
        <button
          className={
            !disabled ? cls.ActionButton : `${cls.ActionButton} ${cls.ActionButtonDisabled}`
          }
          type="submit"
          disabled={disabled}
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            navigate(-1);
          }}
          className={cls.RevokeButton}
        >
          Cancel
        </button>
      </div>
    </Form>
  );
};

export default CreateOrChangeTariffFeatureForm;
